import { useEffect, useRef, useState } from "react";
import { voiceService } from "../../services/voiceService";

// Resposta falada dos Assistentes (RN-570). A síntese leva ~3 s por frase no servidor, então a resposta é
// falada frase a frase: a primeira começa a tocar enquanto a seguinte é gerada. Um elemento de áudio só,
// destravado no clique que liga o modo conversa — no Safari, áudio que não nasce de um toque não toca.

const MAX_CHUNK = 450;
const MIN_CHUNK = 40;
const SILENT_WAV = "data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YQAAAAA=";

const TABLE_ROW = /^\s*\|.*\|\s*$/;

/** Markdown e links não se leem em voz alta: sobra o texto que a pessoa vê. Tabela fica só na tela. */
const toPlainText = (markdown: string) => {
  const lines = markdown.split("\n");
  const hasTable = lines.some((l) => TABLE_ROW.test(l));
  const prose = lines.filter((l) => !TABLE_ROW.test(l)).join("\n");
  return (hasTable ? `${prose}\nDeixei a tabela na tela.` : prose)
    .replace(/\[([^\]]+)\]\([^)]*\)/g, "$1")
    .replace(/https?:\/\/\S+/g, "")
    .replace(/[*_`#>|]/g, "")
    .replace(/^\s*[-•]\s+/gm, "")
    .replace(/[ \t]+/g, " ")
    .trim();
};

/** Frases curtas se juntam (cada chamada tem custo fixo); frase longa demais é cortada na vírgula ou no espaço. */
export const toSpeechChunks = (markdown: string): string[] => {
  const sentences = toPlainText(markdown)
    .split(/(?<=[.!?…:;])\s+|\n+/)
    .map((s) => s.trim())
    .filter(Boolean);
  const chunks: string[] = [];
  for (const sentence of sentences) {
    let rest = sentence;
    while (rest.length > MAX_CHUNK) {
      // O corte entra no trecho (cut + 1): procurar até MAX_CHUNK - 2 mantém o trecho dentro do teto
      const cut = Math.max(rest.lastIndexOf(", ", MAX_CHUNK - 2), rest.lastIndexOf(" ", MAX_CHUNK - 2));
      const at = cut > MIN_CHUNK ? cut + 1 : MAX_CHUNK;
      chunks.push(rest.slice(0, at).trim());
      rest = rest.slice(at).trim();
    }
    const last = chunks[chunks.length - 1];
    if (last && last.length < MIN_CHUNK && last.length + rest.length < 200) chunks[chunks.length - 1] = `${last} ${rest}`;
    else if (rest) chunks.push(rest);
  }
  return chunks;
};

// Resposta longa: a voz diz a introdução e pergunta se lê o resto — o texto inteiro já está na tela
const BRIEF_INTRO_CHARS = 160;
const BRIEF_INTRO_SENTENCES = 2;
const BRIEF_MIN_REST_CHARS = 120;
export const READ_REST_QUESTION = "Quer que eu leia o restante?";
const MAX_FOLLOW_UP_WORDS = 5;
const YES = /^(sim|pode|leia|le|lê|quero|claro|ok|okay|isso|por favor|pode ler|continua|continue)\b/;
const NO = /^(nao|não|chega|para|pare|pode parar|deixa|obrigad[oa]|tudo bem|ta bom|tá bom)\b/;

const sentencesOf = (markdown: string) =>
  toPlainText(markdown).split(/(?<=[.!?…:;])\s+|\n+/).map((s) => s.trim()).filter(Boolean);

/** Introdução (até duas frases, ~160 caracteres) e o restante; resto curto é lido junto. */
export const splitForBrief = (markdown: string): { intro: string; rest: string } => {
  const sentences = sentencesOf(markdown);
  const intro: string[] = [];
  while (sentences.length > 0 && (intro.length === 0 || (intro.length < BRIEF_INTRO_SENTENCES && intro.join(" ").length + sentences[0].length <= BRIEF_INTRO_CHARS))) {
    intro.push(sentences.shift()!);
  }
  const rest = sentences.join(" ");
  return rest.length < BRIEF_MIN_REST_CHARS
    ? { intro: [...intro, rest].filter(Boolean).join(" "), rest: "" }
    : { intro: intro.join(" "), rest };
};

/** Resposta curta à pergunta "Quer que eu leia o restante?"; frase maior é pedido novo. */
export const readFollowUp = (text: string): "read" | "skip" | null => {
  const t = text.toLowerCase().normalize("NFC").replace(/[.!?,]/g, "").trim();
  if (t.split(/\s+/).length > MAX_FOLLOW_UP_WORDS) return null;
  if (NO.test(t)) return "skip";
  if (YES.test(t)) return "read";
  return null;
};

export const useSpeechReply = (areaKey: string, onError: (message: string) => void) => {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  // O restante da última resposta longa, esperando o "sim"
  const pendingRestRef = useRef("");
  const runRef = useRef(0);
  const abortRef = useRef<AbortController | null>(null);
  const endRef = useRef<(() => void) | null>(null);
  const [speaking, setSpeaking] = useState(false);

  const audio = () => {
    if (!audioRef.current) audioRef.current = new Audio();
    return audioRef.current;
  };

  /** Chamar dentro do clique que liga o modo conversa. */
  const unlock = () => {
    const a = audio();
    a.src = SILENT_WAV;
    a.play().catch(() => undefined);
  };

  const stop = () => {
    runRef.current += 1;
    abortRef.current?.abort();
    audioRef.current?.pause();
    endRef.current?.();
    setSpeaking(false);
  };

  useEffect(() => () => { runRef.current += 1; abortRef.current?.abort(); audioRef.current?.pause(); }, []);

  const play = (blob: Blob) =>
    new Promise<void>((resolve) => {
      const a = audio();
      const url = URL.createObjectURL(blob);
      const done = () => { endRef.current = null; URL.revokeObjectURL(url); resolve(); };
      endRef.current = done;
      a.onended = done;
      a.onerror = done;
      a.src = url;
      a.play().catch(done);
    });

  /** Resolve quando terminou de falar ou foi interrompida. Falha da voz vira aviso; o texto já está na tela. */
  const speak = async (markdown: string) => {
    pendingRestRef.current = "";
    stop();
    const run = runRef.current;
    const chunks = toSpeechChunks(markdown);
    if (chunks.length === 0) return;
    const controller = new AbortController();
    abortRef.current = controller;
    const fetchChunk = (text: string) => {
      const p = voiceService.speak(text, areaKey, controller.signal);
      p.catch(() => undefined);
      return p;
    };
    setSpeaking(true);
    try {
      let next = fetchChunk(chunks[0]);
      for (let i = 0; i < chunks.length; i++) {
        const blob = await next;
        if (run !== runRef.current) return;
        if (i + 1 < chunks.length) next = fetchChunk(chunks[i + 1]);
        await play(blob);
        if (run !== runRef.current) return;
      }
    } catch {
      if (run === runRef.current) onError("A voz não respondeu agora; a resposta continua na tela.");
    } finally {
      if (run === runRef.current) setSpeaking(false);
    }
  };

  /** Resposta longa: fala a introdução e pergunta se lê o resto, que fica guardado para o "sim". */
  const speakBrief = async (markdown: string) => {
    const { intro, rest } = splitForBrief(markdown);
    if (!rest) {
      await speak(markdown);
      return;
    }
    await speak(`${intro} ${READ_REST_QUESTION}`);
    pendingRestRef.current = rest;
  };

  /**
   * O que a pessoa disse depois da pergunta: "read" = leu o resto, "skip" = não leu, null = não era resposta à
   * pergunta (vale como pedido novo). Sem resto pendente, sempre null.
   */
  const answerFollowUp = async (text: string): Promise<"read" | "skip" | null> => {
    const rest = pendingRestRef.current;
    if (!rest) return null;
    const answer = readFollowUp(text);
    pendingRestRef.current = "";
    if (answer === "read") await speak(rest);
    return answer;
  };

  return { speaking, speak, speakBrief, answerFollowUp, stop, unlock };
};
